/**
 * Oportunidade (DEMO-03, telas/Oportunidade.tsx), persistente: contexto
 * comercial. O preenchimento econômico acontece DENTRO da Proposta (B3). A
 * Oportunidade mostra os valores da versão VIGENTE quando houver proposta.
 * Resultado (09/10/2026): registrado UMA vez na Proposta (versão vigente), que
 * sincroniza a Oportunidade; sem proposta enviada, só "Registrar perda" 🔒 aqui.
 */
import { ModalConcluirProximaAcao } from './ProximaAcao.tsx';
import { ModalClonarOportunidade } from './Clonagem.tsx';
import { ModalReaberturaComercial, ReaberturasDaOportunidade } from './Retificacao.tsx';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useRotuloTrilha } from '../componentes/trilha.tsx';

import { api } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { dataHora } from '../formatos.ts';
import { ESTAGIOS, alertaAtivo, dataBr, dataEncerramentoRegistro, estagioAberto, motivoEncerramentoExigeObservacao, nomeEstagio, registroForaDeAcompanhamento, statusRegistro, type Estagio } from '../../../backend/src/compartilhado/oportunidades.ts';
import { Abas, Ajuda, Estado, EstadoCarga, Modal, Nota, ResumoIndicadores, SeloSituacao, useCarga } from './ui.tsx';
import { LegendaAlertas, SeloRegistro, SeloTipo, tomEstagio } from './filtros.tsx';
import { Icone } from '../componentes/icones.tsx';
import { CampoData } from './CampoData.tsx';
import { ModalRegistrarAtividade } from './Atividades.tsx';
import { RegistrarPerdaSemProposta, PropostasDaOportunidade } from './proposta/NaOportunidade.tsx';
import { CartaoPropostaVigente, FornecedoresFaturamento, indicadoresDaFicha } from './proposta/FichaVigente.tsx';
import type { DadosProposta } from './proposta/contexto.tsx';
import { versaoVigente } from '../../../backend/src/compartilhado/propostas.ts';
import { CadeiaDaOportunidade } from './Pedidos.tsx';
import { DocumentosDaOportunidade } from './DocumentosCliente.tsx';
import { moeda, type HistoricoLegado, type OpcoesOportunidade, type Oportunidade as TOportunidade, type OportunidadeResumo, type Registro } from './tiposOportunidade.ts';
import { ROTULO_TIPO_ENTIDADE } from './tipos.ts';
import { BotaoExcluir, depoisDeExcluir } from './Lixeira.tsx';

type Aba = 'resumo' | 'registro' | 'propostas' | 'atividades' | 'documentos';

export function Oportunidade({ id }: { id: string }) {
  const { pode } = useSessao();
  const [aba, setAba] = useState<Aba>('resumo');
  const [resultado, setResultado] = useState(false);
  const [registrarAtividade, setRegistrarAtividade] = useState(false);
  const [reagendar, setReagendar] = useState(false);
  const [concluir, setConcluir] = useState(false);
  const [clonar, setClonar] = useState(false);
  const [reabrir, setReabrir] = useState(false);
  const [correcao, setCorrecao] = useState(false);
  // Registro migrado: toda edição exige justificativa (a mudança espera no modal)
  const [pendente, setPendente] = useState<Record<string, unknown> | null>(null);
  // a mesma mudança já enviada (com justificativa) não reabre o modal enquanto os dados não recarregam
  // (o campo de data repete o aviso de mudança no blur, antes do recarregamento)
  const enviado = useRef<string | null>(null);
  const [erro, setErro] = useState('');
  const carga = useCarga(() => Promise.all([api.get<TOportunidade>(`/api/oportunidades/${id}`), api.get<OpcoesOportunidade>('/api/oportunidades/opcoes')]), [id]);
  useRotuloTrilha(carga.dados ? `#${carga.dados[0].numero}` : null);
  const versaoAtual = carga.dados?.[0].atualizado_em;
  useEffect(() => {
    enviado.current = null;
  }, [versaoAtual]);
  // P2-31: Proposta vigente para os indicadores, o cartão e "Fornecedores e faturamento" (mesmos cálculos do pacote 1)
  const verProposta = pode('visualizar', 'proposta');
  const vigente = useCarga(() => (verProposta ? api.get<DadosProposta>(`/api/oportunidades/${id}/proposta`) : Promise.resolve(null)), [id, versaoAtual, verProposta]);
  // revisão 12/10: contagem de documentos ativos na aba Documentos (como "Registro da Oportunidade (n)")
  const verDocumentos = pode('visualizar', 'documento_oportunidade');
  const [versaoDocs, setVersaoDocs] = useState(0);
  const docs = useCarga(() => (verDocumentos ? api.get<{ documentos: { situacao: string }[] }>(`/api/oportunidades/${id}/documentos`) : Promise.resolve(null)), [id, verDocumentos, versaoDocs]);
  const qtdDocumentos = docs.dados?.documentos.filter((d) => d.situacao === 'ativo').length;
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="oportunidade" lixeira={carga.lixeira} />;
  const [o, opcoes] = carga.dados;
  const hoje = opcoes.hoje;
  const aberta = estagioAberto(o.estagio);
  const podeEditar = pode('editar', 'oportunidade') && aberta;
  const faixas = opcoes.faixas_registro;
  const alertas = faixas ? o.registros.filter((r) => alertaAtivo(r, hoje, faixas)) : [];
  const recarregar = () => void carga.recarregar();
  const alterar = async (corpo: Record<string, unknown>, caminho = '') => {
    setErro('');
    if (!caminho && o.migrado && !corpo.justificativa) {
      if (JSON.stringify(corpo) === enviado.current) return;
      return setPendente(corpo);
    }
    try {
      if (caminho) await api.post(`/api/oportunidades/${o.id}/${caminho}`, corpo);
      else await api.patch(`/api/oportunidades/${o.id}`, { ...corpo, atualizado_em: o.atualizado_em });
      recarregar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  return (
    <>
      {/* P2-31: cabeçalho compacto — Cliente | Filial/unidade atendida | Responsável | Data de criação | Previsão */}
      <section className="resumo-destaque" data-teste="resumo-oportunidade">
        <div className="cabecalho-registro">
          <h1 className="titulo-pagina">
            #{o.numero} — {o.titulo}
          </h1>
          <div className="selos-cabecalho">
            <SeloTipo tipo={o.tipo} />
            <span className="selo selo-neutro" data-teste="status-oportunidade">
              Status: {aberta ? 'Em aberto' : nomeEstagio(o.estagio)}
            </span>
            {pode('criar', 'oportunidade') && (
              <button type="button" className="botao-secundario pequeno" onClick={() => setClonar(true)} data-teste="clonar-oportunidade">
                Clonar
              </button>
            )}
            <BotaoExcluir entidade="oportunidade" id={o.id} aoExcluir={depoisDeExcluir('/oportunidades')} />
          </div>
        </div>
        <div className="grade-cabecalho" data-teste="grade-cabecalho-oportunidade">
          <div>
            <span className="rotulo-campo">Cliente</span>
            <a href={`#/clientes/${o.cliente.id}`}>{o.cliente.nome}</a>
          </div>
          <div className="grade-cabecalho-largo">
            <label>
              <span className="rotulo-campo">Filial/unidade atendida</span>
              <select value={o.entidade.id} onChange={(e) => void alterar({ entidade_id: e.target.value })} disabled={!podeEditar} data-teste="entidade-atendida">
                {o.entidades_cliente.map((e) => (
                  <option key={e.id} value={e.id} disabled={e.status === 'inativo'}>
                    {[e.codigo, ROTULO_TIPO_ENTIDADE[e.tipo]].filter(Boolean).join(' · ')}: {e.razao_social} — {e.cnpj}
                    {e.status === 'inativo' ? ' (inativa)' : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div>
            <span className="rotulo-campo">Responsável</span>
            {o.responsavel.nome}
          </div>
          <div>
            <span className="rotulo-campo">Data de criação</span>
            <span>{dataBr(o.data_criacao)}</span>
            {o.data_encerramento && <span className="info-secundaria">Encerrada em {dataBr(o.data_encerramento)}</span>}
          </div>
          <div>
            <CampoData rotulo="Previsão de fechamento" valor={o.previsao_fechamento ?? ''} aoMudar={(x) => x && x !== o.previsao_fechamento && void alterar({ previsao_fechamento: x })} desabilitado={!podeEditar} teste="previsao-oportunidade" classe="campo-data-compacto" />
          </div>
        </div>
      </section>

      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-oportunidade">
          {erro}
        </p>
      )}

      {o.pedidos.length > 0 && (
        <>
          <p className="faixa-pedido-gerado" data-teste="pedido-gerado-oportunidade">
            <strong>Pedido gerado:</strong>{' '}
            {o.pedidos.map((n, i) => (
              <span key={n}>
                {i > 0 && ' · '}
                <a href={`#/pedidos/${n}`}>{n}</a>
                {o.pedido_cancelado === n && <span className="texto-secundario" data-teste="pedido-cancelado-oportunidade"> (cancelado)</span>}
              </span>
            ))}
          </p>
          <CadeiaDaOportunidade numeroPedido={o.pedidos[0]} atual="oportunidade" />
        </>
      )}

      {faixas &&
        alertas.map((r) => (
          <p key={r.id} className={`aviso aviso-alerta prioridade-${statusRegistro(r, hoje, faixas).nivel}`} data-teste="alerta-registro">
            ⚠ <SeloRegistro r={r} faixas={faixas} hoje={hoje} /> — renove ou registre um novo número na aba “Registro da Oportunidade”.
          </p>
        ))}

      {/* P2-31: Estágio junto ao controle (status e validade não se repetem aqui) */}
      <section className="faixa-estagio" data-teste="faixa-estagio">
        <b>Estágio</b> <Ajuda titulo="Estágio">O estágio acompanha o andamento comercial; mudar o estágio não muda a próxima ação nem a proposta. Com proposta enviada, o resultado (ganho ou perda) é registrado na Proposta; sem proposta enviada, a Oportunidade só pode ser encerrada como perdida.</Ajuda>
        {aberta && podeEditar ? (
          <select value={o.estagio} onChange={(e) => void alterar({ estagio: e.target.value as Estagio }, 'estagio')} aria-label="Mover estágio" data-teste="mover-estagio">
            {ESTAGIOS.filter((e) => e.aberto).map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome}
              </option>
            ))}
          </select>
        ) : (
          <span data-teste="estagio-atual">{nomeEstagio(o.estagio)}</span>
        )}
        <span className="espaco" />
        {aberta && pode('registrar_resultado', 'oportunidade') && !(o.proposta && o.proposta.situacao !== 'rascunho') && (
          <button type="button" className="botao-secundario" onClick={() => setResultado(true)} data-teste="registrar-resultado">
            Registrar perda 🔒
          </button>
        )}
      </section>
      {!aberta && o.motivo_encerramento && (
        <p className="aviso aviso-info">
          {nomeEstagio(o.estagio)} — motivo: {o.motivo_encerramento}
          {o.observacao_encerramento ? ` · observação: ${o.observacao_encerramento}` : ''}
        </p>
      )}
      {!aberta && (pode('corrigir_encerrada', 'oportunidade') || pode('reabrir_comercial', 'oportunidade')) && (
        <div className="barra-ferramentas">
          <span className="texto-secundario">Oportunidade encerrada: correções de cadastro são auditadas e não a reabrem.</span>
          <span className="espaco" />
          {pode('corrigir_encerrada', 'oportunidade') && (
            <button type="button" className="botao-secundario" onClick={() => setCorrecao(true)} data-teste="corrigir-encerrada">
              Correção auditada 🔒
            </button>
          )}
          {pode('reabrir_comercial', 'oportunidade') && (
            <button type="button" className="botao-secundario" onClick={() => setReabrir(true)} data-teste="reabertura-comercial">
              Reabertura comercial 🔒
            </button>
          )}
        </div>
      )}

      {/* P2-31: 4 indicadores da vigente; Próxima ação e Proposta vigente lado a lado */}
      <div data-teste="resumo-vigente" className="resumo-ficha">
        {vigente.erro ? <EstadoCarga carregando={false} erro={vigente.erro} recarregar={vigente.recarregar} teste="vigente" /> : <ResumoIndicadores teste="painel-financeiro" itens={indicadoresDaFicha(vigente.dados ?? null, o.valor_estimado)} />}
        <div className="duas-ficha">
          <section className={`cartao-ficha cartao-proxima${o.proxima_acao && o.proxima_acao.data < hoje ? ' vencida' : ''}`} aria-label="Próxima ação">
            <h3>
              <Icone nome="relogio" /> Próxima ação
            </h3>
            {o.proxima_acao ? (
              <div className={`caixa-acao${o.proxima_acao.data < hoje ? ' vencida' : ''}`} data-teste="proxima-acao">
                <strong>{o.proxima_acao.descricao}</strong>
                <span>
                  {o.proxima_acao.responsavel.nome} · {o.proxima_acao.data < hoje ? `⚠ venceu em ${dataBr(o.proxima_acao.data)}` : o.proxima_acao.data === hoje ? 'hoje' : dataBr(o.proxima_acao.data)}
                </span>
                <div className="acoes-linha">
                  {podeEditar && (
                    <button type="button" className="botao-primario pequeno" onClick={() => setConcluir(true)} data-teste="concluir-proxima-acao">
                      Concluir
                    </button>
                  )}
                  {pode('criar', 'atividade') && (
                    <button type="button" className="botao-secundario pequeno" onClick={() => setRegistrarAtividade(true)} data-teste="registrar-atividade-resumo">
                      Registrar atividade
                    </button>
                  )}
                  {podeEditar && (
                    <button type="button" className="botao-secundario pequeno" onClick={() => setReagendar(true)} data-teste="reagendar">
                      Reagendar
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <p className="texto-secundario">{aberta ? 'Sem próxima ação.' : 'Oportunidade encerrada.'}</p>
            )}
          </section>
          <CartaoPropostaVigente
            dados={vigente.dados ?? null}
            acoes={
              vigente.dados?.proposta ? (
                <>
                  <a className="botao-primario pequeno" href={`#/oportunidades/${o.id}/propostas/${vigente.dados.proposta.id}/v/${versaoVigente(vigente.dados.proposta).numero}`} data-teste="abrir-proposta">
                    Abrir proposta
                  </a>
                  <a className="botao-secundario pequeno" href={`#/oportunidades/${o.id}/propostas/${vigente.dados.proposta.id}/pdf/${versaoVigente(vigente.dados.proposta).numero}`}>
                    Visualizar PDF
                  </a>
                  {/* depois da reabertura comercial a vigente ainda é a aceita: o resultado só volta com nova versão enviada */}
                  {aberta && pode('registrar_resultado', 'oportunidade') && o.proposta && ['enviada', 'em_negociacao'].includes(o.proposta.situacao) && (
                    <a className="botao-secundario pequeno" href={`#/oportunidades/${o.id}/propostas/${o.proposta.id}/v/${o.proposta.versao}`} data-teste="resultado-na-proposta">
                      Registrar resultado na Proposta 🔒
                    </a>
                  )}
                </>
              ) : undefined
            }
          />
        </div>
      </div>

      <Abas<Aba>
        rotulo="Seções da oportunidade"
        atual={aba}
        aoTrocar={setAba}
        abas={[
          { id: 'resumo', rotulo: 'Resumo' },
          { id: 'registro', rotulo: `Registro da Oportunidade (${o.registros.length})` },
          { id: 'propostas', rotulo: 'Propostas' },
          { id: 'atividades', rotulo: 'Atividades' },
          { id: 'documentos', rotulo: qtdDocumentos === undefined ? 'Documentos' : `Documentos (${qtdDocumentos})`, desabilitada: verDocumentos ? undefined : 'Sem permissão para ver documentos' },
        ]}
      />

      {aba === 'resumo' && (
        <div className="resumo-aba-ficha">
          {/* P4 bloco 3 (I18): registros vigentes (ativos e renovados) da aba Registro — sem cadastro nem redigitação; vários aparecem todos */}
          {o.registros.some((r) => !registroForaDeAcompanhamento(r)) && (
            <section className="registro-resumo" data-teste="registro-resumo" aria-label="Registro da Oportunidade">
              <h3>
                <Icone nome="documento" /> Registro
              </h3>
              <div className="linhas-registro">
                <div className="registros-vigentes">
                  {o.registros
                    .filter((r) => !registroForaDeAcompanhamento(r))
                    .map((r) => (
                      <div className="registro-linha" key={r.id} data-teste={`registro-resumo-${r.numero}`}>
                        <div className="tile">
                          <span className="rotulo">Número do registro</span>
                          <button type="button" className="botao-link valor" onClick={() => setAba('registro')}>
                            {r.numero}
                          </button>
                        </div>
                        <div className="tile">
                          <span className="rotulo">Fabricante</span>
                          <span className="valor">{r.fabricante || '—'}</span>
                        </div>
                        <div className="tile">
                          <span className="rotulo">Vencimento</span>
                          <span className="valor">{dataBr(r.data_vencimento)}</span>
                        </div>
                      </div>
                    ))}
                </div>
                <button type="button" className="botao-secundario" onClick={() => setAba('registro')} data-teste="ver-registro">
                  Ver registro →
                </button>
              </div>
            </section>
          )}
          {/* D4: só o que tem conteúdo (Origem, Contatos e Escopo quando preenchidos); campos nunca preenchidos e a Data de entrada saem da visualização — os dados continuam guardados */}
          {(() => {
            const pares: [string, string][] = [
              ['Origem', o.origem.origem ?? ''],
              ['Contatos da oportunidade', o.contatos.map((k) => k.nome).join(' · ')],
              ['Escopo', o.escopo ?? ''],
              ['Programa/Referência', o.programa ?? ''],
              ['Campanha', o.origem.campanha ?? ''],
              ['Indicação', o.origem.indicacao ?? ''],
              ['Parceiro', o.origem.parceiro ?? ''],
              ['Observação', o.origem.observacao ?? ''],
            ].filter(([, x]) => String(x).trim() !== '') as [string, string][];
            const extras = (o.historico_legado && (o.historico_legado.registros.length > 0 || o.historico_legado.contratos.length > 0)) || o.tipo !== 'venda';
            if (!pares.length && !extras) return null;
            return (
              <section className="cartao-ficha" data-teste="dados-oportunidade">
                <h3>
                  <Icone nome="documento" /> Dados da oportunidade
                </h3>
                {pares.length > 0 && (
                  <dl className="grade-dados">
                    {pares.map(([k, x]) => (
                      <div key={k} className={k === 'Escopo' || k === 'Observação' ? 'largura-total' : undefined}>
                        <dt>{k}</dt>
                        <dd>{k === 'Escopo' ? <TextoExpansivel texto={x} teste="escopo-oportunidade" /> : x}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                <BlocoDoTipo o={o} />
                {o.historico_legado && (o.historico_legado.registros.length > 0 || o.historico_legado.contratos.length > 0) && <RegistroHistoricoPlanilha h={o.historico_legado} />}
              </section>
            );
          })()}
          <div className="coluna-ficha">
            <section className="cartao-ficha" aria-label="Tarefas e lembretes">
              <h3>Tarefas e lembretes</h3>
              <ul className="lista-simples" data-teste="tarefas">
                {o.tarefas.map((t) => (
                  <li key={t.id}>
                    {t.concluida ? '☑' : '☐'} {t.titulo} <span className="texto-secundario">({t.responsavel_nome}, {dataBr(t.data)})</span>
                  </li>
                ))}
                {faixas &&
                  o.registros
                    .filter((r) => !registroForaDeAcompanhamento(r))
                    .map((r) => (
                      <li key={r.id} data-teste={`lembrete-${r.numero}`}>
                        🔔 Vencimento do registro {r.numero} em {dataBr(r.data_vencimento)} <SeloRegistro r={r} faixas={faixas} hoje={hoje} curto vencimento="oculto" />
                      </li>
                    ))}
                {o.tarefas.length === 0 && o.registros.length === 0 && <li className="texto-secundario">Nenhuma tarefa ou lembrete.</li>}
              </ul>
            </section>
            <section className="cartao-ficha" aria-label="Pedidos gerados">
              <h3>Pedidos gerados</h3>
              {o.pedidos.length ? (
                <ul className="lista-simples" data-teste="pedidos-gerados">
                  {o.pedidos.map((n) => (
                    <li key={n}>
                      <a href={`#/pedidos/${n}`}>{n}</a> <span className="texto-secundario">({o.pedido_cancelado === n ? 'cancelado · ' : ''}versão aceita{o.proposta ? ` da Proposta ${o.proposta.numero}` : ''})</span>
                    </li>
                  ))}
                </ul>
              ) : o.pedido_na_lixeira ? (
                <p className="sinal-lixeira" data-teste="pedido-na-lixeira">
                  Pedido {o.pedido_na_lixeira} na Lixeira
                  <Ajuda titulo="Pedido na Lixeira">{`O Pedido ${o.pedido_na_lixeira} foi enviado à Lixeira. A Oportunidade continua ${nomeEstagio(o.estagio)} e conta em "Vendas ganhas"${o.estagio === 'ganha_parcialmente' ? ' pelo valor do Pedido' : ''}; o Pedido fica fora da lista de Pedidos, dos totais, do ticket médio e do Marketing enquanto estiver na Lixeira. Quem tem "Ver na Lixeira" consulta ou restaura em Administração › Lixeira.`}</Ajuda>
                </p>
              ) : (
                <p className="texto-secundario">Nenhum Pedido.</p>
              )}
            </section>
          </div>
        </div>
      )}
      {aba === 'resumo' && <FornecedoresFaturamento dados={vigente.dados ?? null} />}
      {aba === 'resumo' && o.derivacoes && o.derivacoes.length > 0 && <DerivacoesMigracao derivacoes={o.derivacoes} />}
      {aba === 'resumo' && <ReaberturasDaOportunidade key={o.atualizado_em} oportunidadeId={o.id} />}

      {aba === 'registro' && (faixas ? <RegistrosOportunidade o={o} opcoes={opcoes} podeEditar={podeEditar} aoMudar={recarregar} /> : <Estado tipo="bloqueado" titulo="as faixas de alerta do Registro não estão configuradas neste ambiente" />)}
      {aba === 'propostas' && <PropostasDaOportunidade oportunidadeId={o.id} aoMudar={recarregar} />}
      {aba === 'documentos' && <DocumentosDaOportunidade oportunidadeId={o.id} numero={o.numero} aoMudar={() => (recarregar(), setVersaoDocs((x) => x + 1))} />}
      {aba === 'atividades' && <AtividadesDaOportunidade o={o} aoRegistrar={pode('criar', 'atividade') ? () => setRegistrarAtividade(true) : undefined} />}

      {resultado && <RegistrarPerdaSemProposta oportunidade={o} motivos={opcoes.motivos_perda} aoFechar={() => setResultado(false)} aoSalvar={() => (setResultado(false), recarregar())} />}
      {registrarAtividade && <ModalRegistrarAtividade clienteId={o.cliente.id} oportunidadeId={o.id} aoFechar={() => setRegistrarAtividade(false)} aoSalvar={() => (setRegistrarAtividade(false), recarregar())} />}
      {concluir && o.proxima_acao && <ModalConcluirProximaAcao oportunidade={{ id: o.id, atualizado_em: o.atualizado_em, proxima_acao: o.proxima_acao }} responsaveis={opcoes.responsaveis} aoFechar={() => setConcluir(false)} aoSalvar={() => (setConcluir(false), recarregar())} />}
      {reabrir && <ModalReaberturaComercial oportunidadeId={o.id} aoFechar={() => setReabrir(false)} aoReabrir={() => (setReabrir(false), recarregar())} />}
      {clonar && <ModalClonarOportunidade o={o} responsaveis={opcoes.responsaveis} aoFechar={() => setClonar(false)} />}
      {reagendar && o.proxima_acao && <ModalReagendar o={o} aoFechar={() => setReagendar(false)} aoSalvar={() => (setReagendar(false), recarregar())} />}
      {correcao && <ModalCorrecaoEncerrada o={o} opcoes={opcoes} aoFechar={() => setCorrecao(false)} aoSalvar={() => (setCorrecao(false), recarregar())} />}
      {pendente && (
        <ModalJustificativaMigrado
          aoFechar={() => setPendente(null)}
          aoConfirmar={(justificativa) => {
            const corpo = pendente;
            enviado.current = JSON.stringify(corpo);
            setPendente(null);
            void alterar({ ...corpo, justificativa });
          }}
        />
      )}
    </>
  );
}

/** Reagendar a próxima ação (nova data e, se preciso, nova descrição), com evento automático. */
function ModalReagendar({ o, aoFechar, aoSalvar }: { o: TOportunidade; aoFechar: () => void; aoSalvar: () => void }) {
  const [descricao, setDescricao] = useState(o.proxima_acao!.descricao);
  const [data, setData] = useState(o.proxima_acao!.data);
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');
  const salvar = async () => {
    try {
      await api.patch(`/api/oportunidades/${o.id}`, { proxima_acao: { descricao, data, responsavel_id: o.proxima_acao!.responsavel.id }, atualizado_em: o.atualizado_em, ...(o.migrado ? { justificativa } : {}) });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo="Reagendar a próxima ação"
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" onClick={salvar} disabled={!descricao.trim() || !data || (o.migrado && !justificativa.trim())} data-teste="salvar-reagendar">
            Reagendar
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <label className="campo">
        Próxima ação
        <input value={descricao} onChange={(e) => setDescricao(e.target.value)} data-teste="reagendar-descricao" />
      </label>
      <CampoData rotulo="Nova data" valor={data} aoMudar={setData} teste="reagendar-data" obrigatorio />
      {o.migrado && (
        <label className="campo">
          Justificativa (registro migrado) *
          <textarea value={justificativa} onChange={(e) => setJustificativa(e.target.value)} rows={2} data-teste="reagendar-justificativa" />
        </label>
      )}
    </Modal>
  );
}

function AtividadesDaOportunidade({ o, aoRegistrar }: { o: TOportunidade; aoRegistrar?: () => void }) {
  const concluidas = o.tarefas.filter((t) => t.concluida && t.concluida_em);
  const eventos = [
    ...concluidas.map((t) => ({ chave: `tc-${t.id}`, quando: t.concluida_em!, titulo: `Tarefa concluída: ${t.titulo}`, detalhe: [`tarefa de origem “${t.titulo}”`, `prevista para ${dataBr(t.data)}`, `responsável ${t.responsavel_nome}`, t.origem].filter(Boolean).join(' · '), autor: t.concluida_por_nome ?? '—' })),
    ...o.eventos.map((e, i) => ({ chave: `e-${i}`, quando: e.data_hora, titulo: e.texto, detalhe: '', autor: e.autor ?? 'sistema' })),
  ].sort((a, b) => b.quando.localeCompare(a.quando));
  return (
    <>
      <div className="barra-titulo">
        <h2>Atividades, tarefas e eventos</h2>
        {aoRegistrar && (
          <div className="acoes-direita">
            <button type="button" className="botao-primario" onClick={aoRegistrar} data-teste="registrar-atividade">
              Registrar atividade
            </button>
          </div>
        )}
      </div>
      <p className="legenda-timeline texto-secundario">
        <span className="marca-linha marca-tarefa">◷ Tarefa futura</span> compromisso a cumprir · <span className="marca-linha marca-atividade">✎ Atividade manual</span> algo que alguém fez · <span className="marca-linha marca-evento">⚙ Evento automático</span> registrado pelo sistema
      </p>
      <div className="grade-3">
        <section data-teste="tarefas-oportunidade">
          <h3>◷ Tarefas futuras</h3>
          <ul className="linha-tempo">
            {o.tarefas
              .filter((t) => !t.concluida)
              .map((t) => (
                <li key={t.id} className="item-linha item-tarefa">
                  <strong>{dataBr(t.data)}</strong> · {t.titulo} <span className="texto-secundario">({t.responsavel_nome})</span>
                </li>
              ))}
            {o.tarefas.every((t) => t.concluida) && <li className="texto-secundario">Nenhuma tarefa futura.</li>}
          </ul>
        </section>
        <section data-teste="atividades-manuais">
          <h3>✎ Atividades manuais</h3>
          <ul className="linha-tempo">
            {o.atividades.map((a) => (
              <li key={a.id} className="item-linha item-atividade">
                <strong>{dataHora(a.data_hora).slice(0, 17).replace(',', '')}</strong> · {a.tipo} — {a.resumo}
                {a.resultado ? ` — resultado: ${a.resultado}` : ''} <span className="texto-secundario">({a.autor})</span>
              </li>
            ))}
            {o.atividades.length === 0 && <li className="texto-secundario">Nenhuma atividade.</li>}
          </ul>
        </section>
        <section>
          <h3>⚙ Eventos automáticos</h3>
          <ul className="linha-tempo" data-teste="historico-oportunidade">
            {eventos.map((x) => (
              <li key={x.chave} className="item-linha item-evento">
                <strong>{dataHora(x.quando).slice(0, 17).replace(',', '')}</strong> · {x.titulo} <span className="texto-secundario">{x.detalhe ? `· ${x.detalhe} ` : ''}({x.autor})</span>
              </li>
            ))}
            {eventos.length === 0 && <li className="texto-secundario">Sem registros.</li>}
          </ul>
        </section>
      </div>
    </>
  );
}

/** V-01: Locação e Trade-in só como classificação — campos específicos desabilitados, "a definir". */
function BlocoDoTipo({ o }: { o: TOportunidade }) {
  if (o.tipo === 'venda') return null;
  const locacao = o.tipo === 'locacao';
  return (
    <fieldset className="bloco-a-definir" disabled data-teste="bloco-tipo">
      <legend>{locacao ? 'Condições de locação' : 'Equipamento de entrada (Trade-in)'}</legend>
      <p className="sem-margem">
        Os dados econômicos são preenchidos na <strong>Proposta de {locacao ? 'Locação' : 'Trade-in'}</strong> (aba Propostas): {locacao ? 'parceiro, parcela, periodicidade, prazo e comissão incluída no preço fechado' : 'comprador, avaliação, oferta ao Cliente e comissão'}.
      </p>
      <p className="texto-secundario">Continuam pendentes de contrato próprio (V-01): {locacao ? 'recorrência, reajuste, renovação, cancelamento, reconhecimento da comissão e documento operacional ao parceiro' : 'inspeção e aceite, propriedade, retirada, pagamento e documento operacional ao comprador'}.</p>
    </fieldset>
  );
}

/* ---------- Registro da Oportunidade (deal registration) ---------- */
function RegistrosOportunidade({ o, opcoes, podeEditar, aoMudar }: { o: TOportunidade; opcoes: OpcoesOportunidade; podeEditar: boolean; aoMudar: () => void }) {
  const [renovar, setRenovar] = useState<Registro | null>(null);
  const [novo, setNovo] = useState<Registro | null>(null);
  const [adicionar, setAdicionar] = useState(false);
  const [editar, setEditar] = useState<Registro | null>(null);
  const [encerrar, setEncerrar] = useState<Registro | null>(null);
  const [reabrir, setReabrir] = useState<Registro | null>(null);
  const faixas = opcoes.faixas_registro!;
  const fim = (f: () => void) => () => (f(), aoMudar());
  return (
    <section data-teste="registros">
      <div className="barra-titulo">
        <h2>Registro da Oportunidade</h2>
        {podeEditar && (
          <div className="acoes-direita">
            <button type="button" className="botao-primario" onClick={() => setAdicionar(true)} data-teste="adicionar-registro">
              Adicionar Registro da Oportunidade
            </button>
          </div>
        )}
      </div>
      <LegendaAlertas faixas={faixas} />
      {o.registros.length === 0 ? (
        <p className="vazio">Nenhum registro. O registro é feito com o fabricante (obrigatório), diretamente ou por um parceiro comercial: use “Adicionar Registro da Oportunidade”.</p>
      ) : (
        <div className="rolagem">
          <table className="tabela tabela-compacta">
            <thead>
              <tr>
                <th>Número</th>
                <th>Fabricante</th>
                <th>Parceiro comercial</th>
                <th>Registro</th>
                <th>Vencimento</th>
                <th>Alerta</th>
                <th>Observação</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {o.registros.map((r) => (
                <tr key={r.id} data-teste={`registro-${r.numero}`}>
                  <td>{r.numero}</td>
                  <td data-teste={`fabricante-${r.numero}`}>{r.fabricante_parceiro_id ? <a href={`#/administracao/parceiros/${r.fabricante_parceiro_id}`}>{r.fabricante}</a> : r.fabricante}</td>
                  <td data-teste={`parceiro-${r.numero}`}>{r.parceiro_id ? <a href={`#/administracao/parceiros/${r.parceiro_id}`}>{r.parceiro_nome}</a> : <span className="texto-secundario">Direto com o fabricante</span>}</td>
                  <td className="nowrap">{dataBr(r.data_registro)}</td>
                  <td className="nowrap">
                    <strong>{dataBr(r.data_vencimento)}</strong>
                  </td>
                  <td>
                    <SeloRegistro r={r} faixas={faixas} hoje={opcoes.hoje} curto vencimento="oculto" />
                  </td>
                  <td>
                    {r.observacao || '—'}
                    {r.situacao === 'encerrado' && r.observacao_encerramento && <div className="texto-secundario">Encerramento: {r.observacao_encerramento}</div>}
                  </td>
                  <td>
                    {podeEditar && !registroForaDeAcompanhamento(r) && (
                      <div className="acoes-registro">
                        <button type="button" className="botao-secundario pequeno" onClick={() => setRenovar(r)} data-teste={`renovar-${r.numero}`}>
                          Renovar
                        </button>
                        <button type="button" className="botao-secundario pequeno" onClick={() => setNovo(r)} data-teste={`novo-numero-${r.numero}`}>
                          Registrar novo número
                        </button>
                        <button type="button" className="botao-secundario pequeno" onClick={() => setEditar(r)} data-teste={`editar-registro-${r.numero}`}>
                          Editar
                        </button>
                        <button type="button" className="botao-secundario pequeno" onClick={() => setEncerrar(r)} data-teste={`encerrar-registro-${r.numero}`}>
                          Encerrar registro
                        </button>
                      </div>
                    )}
                    {podeEditar && r.situacao === 'encerrado' && (
                      <div className="acoes-registro">
                        <button type="button" className="botao-secundario pequeno" onClick={() => setReabrir(r)} data-teste={`reabrir-registro-${r.numero}`}>
                          Reabrir
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h3>Histórico dos registros</h3>
      <ul className="linha-tempo" data-teste="historico-registros">
        {o.registros.flatMap((r) =>
          (r.historico ?? []).map((h, i) => (
            <li key={`${r.id}-${i}`}>
              <strong>{dataBr(h.data_hora.slice(0, 10))}</strong> · {r.numero}: {h.texto} <span className="texto-secundario">({h.autor ?? 'sistema'})</span>
            </li>
          )),
        )}
      </ul>
      <Nota>
        Faixas de alerta ({faixas[0]}, {faixas[1]} e {faixas[2]} dias) configuradas em Parâmetros.
      </Nota>
      {adicionar && <ModalNovoRegistro o={o} aoFechar={() => setAdicionar(false)} aoSalvar={fim(() => setAdicionar(false))} />}
      {renovar && <ModalRenovar r={renovar} aoFechar={() => setRenovar(null)} aoSalvar={fim(() => setRenovar(null))} />}
      {novo && <ModalNovoNumero r={novo} opcoes={opcoes} aoFechar={() => setNovo(null)} aoSalvar={fim(() => setNovo(null))} />}
      {editar && <ModalEditarRegistro r={editar} opcoes={opcoes} aoFechar={() => setEditar(null)} aoSalvar={fim(() => setEditar(null))} />}
      {encerrar && <ModalEncerrarRegistro r={encerrar} opcoes={opcoes} aoFechar={() => setEncerrar(null)} aoSalvar={fim(() => setEncerrar(null))} />}
      {reabrir && <ModalReabrirRegistro r={reabrir} aoFechar={() => setReabrir(null)} aoSalvar={fim(() => setReabrir(null))} />}
    </section>
  );
}

function ModalRenovar({ r, aoFechar, aoSalvar }: { r: Registro; aoFechar: () => void; aoSalvar: () => void }) {
  const [data, setData] = useState('');
  const [erro, setErro] = useState('');
  const confirmar = async () => {
    try {
      await api.post(`/api/registros-oportunidade/${r.id}/renovar`, { data_vencimento: data });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Renovar registro ${r.numero}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!(data > r.data_vencimento)} onClick={confirmar} data-teste="confirmar-renovacao">
            Confirmar renovação
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <p>Vencimento atual: {dataBr(r.data_vencimento)}. O vencimento anterior fica no histórico.</p>
      <CampoData rotulo="Novo vencimento" valor={data} aoMudar={setData} min={r.data_vencimento} teste="novo-vencimento" obrigatorio />
    </Modal>
  );
}

/**
 * Pacote 5: parceiro comercial do Registro (opcional — nulo = direto com o fabricante). Só parceiros ativos com papel
 * Distribuidor ou Grupo comercial; o atual continua visível mesmo que tenha deixado de se qualificar.
 */
function CampoParceiroRegistro({ valor, aoMudar, opcoes, atual, teste }: { valor: string; aoMudar: (v: string) => void; opcoes: OpcoesOportunidade; atual?: { id: string; nome: string } | null; teste: string }) {
  const lista = atual && !opcoes.parceiros_registro.some((x) => x.id === atual.id) ? [...opcoes.parceiros_registro, { ...atual, papeis: [] as string[] }] : opcoes.parceiros_registro;
  return (
    <select value={valor} onChange={(e) => aoMudar(e.target.value)} data-teste={teste}>
      <option value="">Nenhum — registro direto com o fabricante</option>
      {lista.map((x) => (
        <option key={x.id} value={x.id}>
          {x.nome}
          {x.papeis.length ? ` (${x.papeis.join(' · ')})` : ''}
        </option>
      ))}
    </select>
  );
}

function ModalNovoNumero({ r, opcoes, aoFechar, aoSalvar }: { r: Registro; opcoes: OpcoesOportunidade; aoFechar: () => void; aoSalvar: () => void }) {
  const hoje = opcoes.hoje;
  const [numero, setNumero] = useState('');
  const [data, setData] = useState('');
  const [parceiro, setParceiro] = useState(r.parceiro_id ?? '');
  const [erro, setErro] = useState('');
  const ok = numero.trim().length > 3 && data > hoje;
  const confirmar = async () => {
    try {
      await api.post(`/api/registros-oportunidade/${r.id}/substituir`, { numero: numero.trim(), data_vencimento: data, parceiro_id: parceiro || null });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Registrar novo número (substitui ${r.numero})`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!ok} onClick={confirmar} data-teste="confirmar-novo-numero">
            Registrar novo número
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <p>O registro {r.numero} fica como “Substituído”, preservado no histórico.</p>
      <label className="campo">
        Novo número do registro
        <input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="ex.: DR-0001" data-teste="novo-numero" />
      </label>
      <CampoData rotulo="Vencimento" valor={data} aoMudar={setData} min={hoje} teste="vencimento-novo-numero" obrigatorio />
      <label className="campo">
        Parceiro comercial (opcional)
        <CampoParceiroRegistro valor={parceiro} aoMudar={setParceiro} opcoes={opcoes} atual={r.parceiro_id ? { id: r.parceiro_id, nome: r.parceiro_nome ?? '—' } : null} teste="parceiro-novo-numero" />
      </label>
    </Modal>
  );
}

/** Pacote 5: editar o Registro — só o parceiro comercial e a observação (número, fabricante, data e vencimento não mudam). */
function ModalEditarRegistro({ r, opcoes, aoFechar, aoSalvar }: { r: Registro; opcoes: OpcoesOportunidade; aoFechar: () => void; aoSalvar: () => void }) {
  const [parceiro, setParceiro] = useState(r.parceiro_id ?? '');
  const [obs, setObs] = useState(r.observacao ?? '');
  const [erro, setErro] = useState('');
  const mudou = (parceiro || null) !== (r.parceiro_id ?? null) || obs.trim() !== (r.observacao ?? '').trim();
  const confirmar = async () => {
    setErro('');
    try {
      await api.patch(`/api/registros-oportunidade/${r.id}`, { parceiro_id: parceiro || null, observacao: obs.trim(), atualizado_em: r.atualizado_em });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Editar registro ${r.numero}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={!mudou} onClick={confirmar} data-teste="confirmar-editar-registro">
            Salvar alterações
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-editar-registro">
          {erro}
        </p>
      )}
      <div className="grade-form sem-borda">
        <label>
          Número do registro
          <input value={r.numero} disabled data-teste="editar-registro-numero" />
        </label>
        <label>
          Fabricante
          <input value={r.fabricante} disabled />
        </label>
        <label>
          Data do registro
          <input value={dataBr(r.data_registro)} disabled />
        </label>
        <label>
          Vencimento
          <input value={dataBr(r.data_vencimento)} disabled />
        </label>
        <label className="largura-total">
          Parceiro comercial (opcional)
          <CampoParceiroRegistro valor={parceiro} aoMudar={setParceiro} opcoes={opcoes} atual={r.parceiro_id ? { id: r.parceiro_id, nome: r.parceiro_nome ?? '—' } : null} teste="editar-registro-parceiro" />
        </label>
        <label className="largura-total">
          Observação
          <input value={obs} onChange={(e) => setObs(e.target.value)} maxLength={1000} data-teste="editar-registro-observacao" />
        </label>
      </div>
      <p className="texto-secundario">Número, fabricante, data e vencimento não mudam aqui: use “Registrar novo número” ou “Renovar”. A alteração fica no histórico do registro.</p>
    </Modal>
  );
}

/** Pacote 5: encerrar o Registro — motivo da lista (Outro exige observação). O estágio da Oportunidade não muda. */
function ModalEncerrarRegistro({ r, opcoes, aoFechar, aoSalvar }: { r: Registro; opcoes: OpcoesOportunidade; aoFechar: () => void; aoSalvar: () => void }) {
  const motivos = opcoes.motivos_encerramento_registro ?? [];
  const [motivo, setMotivo] = useState('');
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState('');
  const exigeObs = motivoEncerramentoExigeObservacao(motivo);
  const ok = Boolean(motivo) && (!exigeObs || obs.trim().length > 0);
  const confirmar = async () => {
    setErro('');
    try {
      await api.post(`/api/registros-oportunidade/${r.id}/encerrar`, { motivo, observacao: obs.trim() || undefined, atualizado_em: r.atualizado_em });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Encerrar registro ${r.numero}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!ok} onClick={confirmar} data-teste="confirmar-encerrar-registro">
            Encerrar registro
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-encerrar-registro">
          {erro}
        </p>
      )}
      <p>O registro deixa de gerar alertas e lembretes. O estágio da Oportunidade não muda; o registro pode ser reaberto depois, com justificativa.</p>
      <label className="campo">
        Motivo *
        <select value={motivo} onChange={(e) => setMotivo(e.target.value)} data-teste="encerrar-registro-motivo">
          <option value="">Selecione…</option>
          {motivos.map((m) => (
            <option key={m} value={m}>
              {motivoEncerramentoExigeObservacao(m) ? `${m} (observação obrigatória)` : m}
            </option>
          ))}
        </select>
      </label>
      <label className="campo">
        {exigeObs ? 'Observação *' : 'Observação'}
        <textarea rows={3} value={obs} onChange={(e) => setObs(e.target.value)} maxLength={1000} required={exigeObs} data-teste="encerrar-registro-observacao" />
      </label>
    </Modal>
  );
}

/** Pacote 5: reabrir o Registro encerrado (justificativa com pelo menos 10 caracteres); volta a gerar alertas. */
function ModalReabrirRegistro({ r, aoFechar, aoSalvar }: { r: Registro; aoFechar: () => void; aoSalvar: () => void }) {
  const [just, setJust] = useState('');
  const [erro, setErro] = useState('');
  const ok = just.trim().length >= 10;
  const confirmar = async () => {
    setErro('');
    try {
      await api.post(`/api/registros-oportunidade/${r.id}/reabrir`, { justificativa: just.trim(), atualizado_em: r.atualizado_em });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Reabrir registro ${r.numero}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!ok} onClick={confirmar} data-teste="confirmar-reabrir-registro">
            Reabrir registro
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-reabrir-registro">
          {erro}
        </p>
      )}
      <p>
        Encerrado em {dataEncerramentoRegistro(r.encerrado_em)}
        {r.motivo_encerramento ? ` · ${r.motivo_encerramento}` : ''}. Ao reabrir, o registro volta a “Ativo” e aos alertas pelo vencimento ({dataBr(r.data_vencimento)}).
      </p>
      <label className="campo">
        Justificativa * (mínimo de 10 caracteres)
        <textarea rows={3} value={just} onChange={(e) => setJust(e.target.value)} maxLength={1000} data-teste="reabrir-registro-justificativa" />
      </label>
    </Modal>
  );
}

/**
 * Adicionar Registro da Oportunidade. Regra: FABRICANTE obrigatório (lista do catálogo e dos parceiros com papel
 * Fabricante, ou "Outro" com nome livre — sem criar parceiro); PARCEIRO COMERCIAL opcional (registro direto com o
 * fabricante). O mesmo registro aparece na visão do parceiro (e do fabricante cadastrado), sem cópia.
 * Aberto na visão de um parceiro (`parceiroId`): escolhe-se a Oportunidade em aberto; se o parceiro for
 * Fabricante, ele já vem como fabricante; nos demais casos, como parceiro comercial.
 */
export function ModalNovoRegistro({ o, parceiroId, aoFechar, aoSalvar }: { o?: { id: string; numero: string; titulo: string }; parceiroId?: string; aoFechar: () => void; aoSalvar: () => void }) {
  const carga = useCarga(() => Promise.all([api.get<OpcoesOportunidade>('/api/oportunidades/opcoes'), o ? Promise.resolve([o]) : api.get<OportunidadeResumo[]>('/api/oportunidades').then((l) => l.filter((x) => estagioAberto(x.estagio)))]), [o?.id]);
  if (!carga.dados)
    return (
      <Modal titulo="Adicionar Registro da Oportunidade" aoFechar={aoFechar} rodape={null}>
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} />
      </Modal>
    );
  const [opcoes, oportunidades] = carga.dados;
  return <FormRegistro oportunidades={oportunidades} fixa={Boolean(o)} parceiroId={parceiroId} opcoes={opcoes} aoFechar={aoFechar} aoSalvar={aoSalvar} />;
}

function FormRegistro({ oportunidades, fixa, parceiroId, opcoes, aoFechar, aoSalvar }: { oportunidades: { id: string; numero: string; titulo: string }[]; fixa: boolean; parceiroId?: string; opcoes: OpcoesOportunidade; aoFechar: () => void; aoSalvar: () => void }) {
  const fabricanteDoParceiro = parceiroId ? opcoes.fabricantes.find((f) => f.parceiro_id === parceiroId) : undefined;
  const [op, setOp] = useState(fixa ? oportunidades[0].id : '');
  const [g, setG] = useState(fabricanteDoParceiro ? '' : (parceiroId ?? ''));
  const [fab, setFab] = useState(fabricanteDoParceiro?.chave ?? '');
  const [fabOutro, setFabOutro] = useState('');
  const [numero, setNumero] = useState('');
  const [registro, setRegistro] = useState(opcoes.hoje);
  const [vencimento, setVencimento] = useState('');
  const [obs, setObs] = useState('');
  const [faltam, setFaltam] = useState<string[]>([]);
  const escolhido = opcoes.fabricantes.find((f) => f.chave === fab);
  const salvar = async () => {
    const f = [!op && 'Selecione a Oportunidade.', !fab && 'Informe o fabricante.', fab === 'outro' && !fabOutro.trim() && 'Digite o nome do fabricante (Outro).', numero.trim().length < 4 && 'Informe o número do registro.', !vencimento && 'Informe o vencimento.', vencimento && registro && vencimento <= registro && 'O vencimento precisa ser posterior à data do registro.'].filter(Boolean) as string[];
    setFaltam(f);
    if (f.length) return;
    try {
      await api.post(`/api/oportunidades/${op}/registros`, {
        numero: numero.trim(),
        parceiro_id: g || undefined,
        fabricante_parceiro_id: escolhido?.parceiro_id,
        fabricante: fab === 'outro' ? fabOutro.trim() : escolhido?.nome,
        data_registro: registro,
        data_vencimento: vencimento,
        observacao: obs.trim(),
      });
      aoSalvar();
    } catch (e) {
      setFaltam([(e as Error).message]);
    }
  };
  return (
    <Modal
      titulo="Adicionar Registro da Oportunidade"
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" onClick={salvar} data-teste="salvar-registro">
            Adicionar registro
          </button>
        </>
      }
    >
      <div className="grade-form sem-borda">
        <label className="largura-total">
          Oportunidade
          <select value={op} onChange={(e) => setOp(e.target.value)} disabled={fixa} data-teste="registro-oportunidade">
            {!fixa && <option value="">Selecione…</option>}
            {oportunidades.map((x) => (
              <option key={x.id} value={x.id}>
                #{x.numero} — {x.titulo}
              </option>
            ))}
          </select>
        </label>
        <label className="coluna-dupla">
          Fabricante *
          <select value={fab} onChange={(e) => setFab(e.target.value)} data-teste="registro-fabricante">
            <option value="">Selecione…</option>
            <optgroup label="Parceiros com papel Fabricante">
              {opcoes.fabricantes
                .filter((f) => f.origem === 'parceiro')
                .map((f) => (
                  <option key={f.chave} value={f.chave}>
                    {f.nome}
                  </option>
                ))}
            </optgroup>
            <optgroup label="Fabricantes do catálogo">
              {opcoes.fabricantes
                .filter((f) => f.origem === 'catalogo')
                .map((f) => (
                  <option key={f.chave} value={f.chave}>
                    {f.nome}
                  </option>
                ))}
            </optgroup>
            <option value="outro">Outro (digitar o nome)</option>
          </select>
        </label>
        {fab === 'outro' ? (
          <label>
            Nome do fabricante *
            <input value={fabOutro} onChange={(e) => setFabOutro(e.target.value)} placeholder="ex.: Fabricante Exemplo" data-teste="registro-fabricante-outro" />
            <small className="ajuda">Fica só no Registro e no histórico; não cria parceiro no cadastro.</small>
          </label>
        ) : (
          <span />
        )}
        <label className="coluna-dupla">
          Parceiro comercial (opcional)
          <CampoParceiroRegistro valor={g} aoMudar={setG} opcoes={opcoes} teste="registro-parceiro" />
        </label>
        <label>
          Número do registro
          <input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="ex.: DR-0001" data-teste="registro-numero" />
        </label>
        <CampoData rotulo="Data do registro" valor={registro} aoMudar={setRegistro} teste="registro-data" obrigatorio />
        <CampoData rotulo="Vencimento" valor={vencimento} aoMudar={setVencimento} min={registro || opcoes.hoje} teste="registro-vencimento" obrigatorio />
        <label className="largura-total">
          Observação
          <input value={obs} onChange={(e) => setObs(e.target.value)} />
        </label>
      </div>
      {faltam.length > 0 && <Estado tipo="bloqueado" titulo="Falta completar" itens={faltam} teste="faltam-registro" />}
      <p className="texto-secundario">
        Alertas progressivos (aviso {opcoes.faixas_registro?.[0]}, atenção {opcoes.faixas_registro?.[1]}, urgente {opcoes.faixas_registro?.[2]} dias, crítico vencido). O mesmo registro aparece na visão do parceiro comercial, sem cópia.
      </p>
    </Modal>
  );
}

/** Registro migrado: a edição só é gravada com justificativa (fica na auditoria com valor anterior e novo). */
function ModalJustificativaMigrado({ aoFechar, aoConfirmar }: { aoFechar: () => void; aoConfirmar: (justificativa: string) => void }) {
  const [texto, setTexto] = useState('');
  return (
    <Modal
      titulo="Justificativa da correção"
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" onClick={() => aoConfirmar(texto.trim())} disabled={!texto.trim()} data-teste="confirmar-justificativa-migrado">
            Salvar
          </button>
        </>
      }
    >
      <Nota>Esta Oportunidade veio da migração. Toda correção exige justificativa e fica na auditoria (valor anterior, novo, usuário e data/hora).</Nota>
      <label className="campo">
        Justificativa *
        <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={3} data-teste="justificativa-migrado" />
      </label>
    </Modal>
  );
}

/**
 * Correção auditada de Oportunidade ENCERRADA (aprovação de 27/09/2026): NÃO reabre, não volta ao Kanban, não cria
 * versão e não toca snapshots, Pedido, Ordem ou faturamento. Só os campos de cadastro abaixo, com justificativa.
 */
function ModalCorrecaoEncerrada({ o, opcoes, aoFechar, aoSalvar }: { o: TOportunidade; opcoes: OpcoesOportunidade; aoFechar: () => void; aoSalvar: () => void }) {
  const [titulo, setTitulo] = useState(o.titulo);
  const [escopo, setEscopo] = useState(o.escopo ?? '');
  const [origem, setOrigem] = useState(o.origem.origem ?? '');
  const [responsavel, setResponsavel] = useState(o.responsavel.id);
  const [motivo, setMotivo] = useState(o.motivo_encerramento ?? '');
  const [dataEnc, setDataEnc] = useState(o.data_encerramento ?? '');
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');
  const corpo: Record<string, unknown> = {};
  if (titulo.trim() !== o.titulo) corpo.titulo = titulo.trim();
  if (escopo.trim() !== (o.escopo ?? '')) corpo.escopo = escopo.trim();
  if (origem.trim() !== (o.origem.origem ?? '')) corpo.origem = origem.trim();
  if (responsavel !== o.responsavel.id) corpo.responsavel_id = responsavel;
  if (o.estagio === 'perdida' && motivo !== (o.motivo_encerramento ?? '')) corpo.motivo_encerramento = motivo;
  if (dataEnc !== (o.data_encerramento ?? '')) corpo.data_encerramento = dataEnc || null;
  const mudou = Object.keys(corpo).length > 0;
  const salvar = async () => {
    setErro('');
    try {
      await api.post(`/api/oportunidades/${o.id}/correcao`, { ...corpo, justificativa: justificativa.trim(), atualizado_em: o.atualizado_em });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Correção auditada — #${o.numero}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" onClick={salvar} disabled={!mudou || !justificativa.trim()} data-teste="salvar-correcao">
            Registrar correção
          </button>
        </>
      }
    >
      <Nota>
        A Oportunidade continua <strong>{nomeEstagio(o.estagio)}</strong>: a correção não reabre, não volta ao Kanban, não cria versão e não altera a Proposta, o Pedido, as Ordens nem o faturamento. Cada campo alterado vira evento na
        Timeline e linha na auditoria.
      </Nota>
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-correcao">
          {erro}
        </p>
      )}
      <label className="campo">
        Título
        <input value={titulo} onChange={(e) => setTitulo(e.target.value)} data-teste="correcao-titulo" />
      </label>
      <label className="campo">
        Escopo
        <textarea value={escopo} onChange={(e) => setEscopo(e.target.value)} rows={3} data-teste="correcao-escopo" />
      </label>
      <label className="campo">
        Origem
        <input value={origem} onChange={(e) => setOrigem(e.target.value)} data-teste="correcao-origem" />
      </label>
      <label className="campo">
        Responsável
        <select value={responsavel} onChange={(e) => setResponsavel(e.target.value)} data-teste="correcao-responsavel">
          {!opcoes.responsaveis.some((r) => r.id === o.responsavel.id) && <option value={o.responsavel.id}>{o.responsavel.nome}</option>}
          {opcoes.responsaveis.map((r) => (
            <option key={r.id} value={r.id}>
              {r.nome}
            </option>
          ))}
        </select>
      </label>
      {o.estagio === 'perdida' && (
        <label className="campo">
          Motivo da perda
          <select value={motivo} onChange={(e) => setMotivo(e.target.value)} data-teste="correcao-motivo">
            {!opcoes.motivos_perda.includes(motivo) && <option value={motivo}>{motivo || '—'}</option>}
            {opcoes.motivos_perda.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
      )}
      <CampoData rotulo={o.migrado ? 'Data de encerramento (vazia = não informada na origem)' : 'Data de encerramento'} valor={dataEnc} aoMudar={(x) => setDataEnc(x ?? '')} teste="correcao-data-encerramento" />
      <label className="campo">
        Justificativa *
        <textarea value={justificativa} onChange={(e) => setJustificativa(e.target.value)} rows={3} data-teste="correcao-justificativa" />
      </label>
    </Modal>
  );
}

/**
 * Texto longo limitado a 2 linhas com "ver tudo" / "ver menos" (mockup p2-12b: Escopo). O link só aparece quando o
 * texto realmente passa do limite; o texto completo também fica na dica (title).
 */
function TextoExpansivel({ texto, teste }: { texto: string; teste: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [aberto, setAberto] = useState(false);
  const [corta, setCorta] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || aberto) return;
    const medir = () => setCorta(el.scrollHeight > el.clientHeight + 1);
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    return () => obs.disconnect();
  }, [texto, aberto]);
  return (
    <span className="texto-expansivel">
      <span ref={ref} className={aberto ? undefined : 'duas-linhas'} title={aberto ? undefined : texto} data-teste={teste}>
        {texto}
      </span>
      {(corta || aberto) && (
        <button type="button" className="botao-link" onClick={() => setAberto(!aberto)} aria-expanded={aberto} data-teste={`${teste}-ver-tudo`}>
          {aberto ? 'ver menos' : 'ver tudo'}
        </button>
      )}
    </span>
  );
}

/** Nome de leitura do campo derivado; o nome técnico e a regra ficam só na consulta (rastreabilidade). */
const ROTULO_DERIVACAO: Record<string, string> = {
  data_criacao: 'Data de criação',
  previsao_fechamento: 'Previsão de fechamento',
  data_encerramento: 'Data de encerramento',
  valor_estimado: 'Valor estimado',
};

/**
 * Valores obtidos por REGRA da migração (não informados pelo usuário). Revisão 12/10: fora da leitura principal —
 * consulta recolhida "Origem dos dados migrados", com o campo técnico e a regra aplicada para rastreabilidade.
 */
function DerivacoesMigracao({ derivacoes }: { derivacoes: NonNullable<TOportunidade['derivacoes']> }) {
  return (
    <details className="consulta-recolhida" data-teste="derivacoes-migracao">
      <summary>
        Origem dos dados migrados <span className="texto-secundario">· {derivacoes.length} {derivacoes.length === 1 ? 'valor derivado' : 'valores derivados'}</span>
      </summary>
      <ul className="lista-simples">
        {derivacoes.map((d) => (
          <li key={d.campo}>
            <strong>{ROTULO_DERIVACAO[d.campo] ?? d.campo}</strong>: {d.descricao}
            <br />
            <span className="texto-secundario">
              campo {d.campo} · regra {d.regra}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

const lit = (v: string | number | null | undefined) => (v === null || v === undefined || v === '' ? '—' : String(v));
const litMoeda = (v: number | null) => (v === null ? '—' : moeda(v, 2));

/**
 * Registro histórico da planilha (somente leitura): propostas alternativas/históricas e contratos de Locação com os
 * valores LITERAIS da origem. Nada daqui entra nos indicadores do HUB.
 */
function RegistroHistoricoPlanilha({ h }: { h: HistoricoLegado }) {
  return (
    <div className="bloco-dados" data-teste="registro-historico-planilha">
      <h3>Registro histórico da planilha</h3>
      <p className="texto-secundario sem-margem">Somente leitura. Valores como estavam na planilha de origem; não entram nos indicadores.</p>
      {h.registros.map((r) => (
        <details key={r.id} className="bloco-dados" data-teste={`historico-proposta-${r.proposta_numero}`}>
          <summary>
            Proposta {r.proposta_numero}
            {r.versao ? ` v${r.versao}` : ''} — {r.tipo_origem} {r.papel === 'alternativa' ? `(alternativa da ${r.alternativa_de})` : '(histórica)'}
          </summary>
          <dl className="grade-dados">
            <div>
              <dt>Data da proposta</dt>
              <dd>{r.data_proposta ? dataBr(r.data_proposta) : '—'}</dd>
            </div>
            <div>
              <dt>Validade (dias)</dt>
              <dd>{lit(r.validade_dias)}</dd>
            </div>
            <div>
              <dt>Situação (planilha)</dt>
              <dd>{lit(r.situacao_origem)}</dd>
            </div>
            <div>
              <dt>Condição (planilha)</dt>
              <dd>{lit(r.forma_pagamento_texto)}</dd>
            </div>
            <div>
              <dt>Modalidade / prazo</dt>
              <dd>{[r.modalidade, r.prazo_meses ? `${r.prazo_meses} meses` : null].filter(Boolean).join(' · ') || '—'}</dd>
            </div>
            <div>
              <dt>Frete (planilha)</dt>
              <dd>{[r.frete_texto, r.valor_frete_origem !== null ? litMoeda(r.valor_frete_origem) : null].filter(Boolean).join(' · ') || '—'}</dd>
            </div>
            <div>
              <dt>Parceiro (planilha)</dt>
              <dd>{lit(r.parceiro)}</dd>
            </div>
            <div>
              <dt>Contato</dt>
              <dd>{lit(r.contato)}</dd>
            </div>
          </dl>
          {r.itens.length > 0 && (
            <div className="tabela-rolavel">
              <table className="tabela">
                <thead>
                  <tr>
                    <th>Linha</th>
                    <th>PN</th>
                    <th>Descrição</th>
                    <th>Tipo (planilha)</th>
                    <th>Qtd.</th>
                    <th>Moeda</th>
                    <th>Custo unit. R$ (planilha)</th>
                    <th>Venda unit. (planilha)</th>
                    <th>Total linha (planilha)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {r.itens.map((i) => (
                    <tr key={i.linha}>
                      <td>{i.linha}</td>
                      <td>{lit(i.pn)}</td>
                      <td>{lit(i.descricao)}</td>
                      <td>{lit(i.tipo_item)}</td>
                      <td>{lit(i.quantidade)}</td>
                      <td>{lit(i.moeda)}</td>
                      <td>{litMoeda(i.custo_unit_reais)}</td>
                      <td>{litMoeda(i.venda_unit)}</td>
                      <td>{litMoeda(i.total_linha)}</td>
                      <td>{[i.status, i.motivo].filter(Boolean).join(' — ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {r.observacao && <p className="texto-secundario">{r.observacao}</p>}
        </details>
      ))}
      {h.contratos.map((c) => (
        <details key={c.ped_legado} className="bloco-dados" data-teste={`historico-contrato-${c.ped_legado}`}>
          <summary>
            Contrato {c.ped_legado} — {c.status_origem} (planilha)
          </summary>
          <dl className="grade-dados">
            <div>
              <dt>Data do pedido (planilha)</dt>
              <dd>{c.data_pedido ? dataBr(c.data_pedido) : '—'}</dd>
            </div>
            <div>
              <dt>OC do Cliente</dt>
              <dd>{lit(c.oc_cliente)}</dd>
            </div>
            <div>
              <dt>Pedido ao parceiro</dt>
              <dd>{lit(c.pedido_parceiro)}</dd>
            </div>
            <div>
              <dt>Parceiro (planilha)</dt>
              <dd>{lit(c.parceiro)}</dd>
            </div>
            <div>
              <dt>Condição (planilha)</dt>
              <dd>{lit(c.condicao)}</dd>
            </div>
            <div>
              <dt>Valor total (planilha)</dt>
              <dd>{litMoeda(c.valor_total_origem)}</dd>
            </div>
            <div>
              <dt>Custo total (planilha)</dt>
              <dd>{litMoeda(c.custo_total_origem)}</dd>
            </div>
            <div>
              <dt>Receita (planilha)</dt>
              <dd>{[c.regra_receita, c.parcelas_efetivas ? `${c.parcelas_efetivas} parcelas` : null].filter(Boolean).join(' · ') || '—'}</dd>
            </div>
          </dl>
        </details>
      ))}
    </div>
  );
}
